import { z } from 'zod';
import { ODOO_FIELD_NAME_PATTERN } from './odoo-settings';
import { odooModuleIdSchema } from './odoo-timeoff';

/**
 * Odoo compatibility check (plan §5.1, owner): before any Odoo procedure, the server checks
 * with `fields_get` and `check_access_rights` that what the tile needs exists. A missing
 * required item → 409 `ODOO_NOT_COMPATIBLE` with `odooNotCompatibleDetailsSchema` details; a
 * missing optional item (a custom field named in module settings) switches only that part off.
 * Test connection shows `odooCompatReportSchema`. Messages are plain words, never Odoo's text.
 */

/** An Odoo model name, e.g. `hr.leave.allocation`. */
export const ODOO_MODEL_NAME_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/;
const model = z.string().regex(ODOO_MODEL_NAME_PATTERN);
const message = z.string().min(1).max(300);

/** The access rights the middleware needs: it only reads and creates. */
export const ODOO_ACCESS_OPERATIONS = ['read', 'create'] as const;

export const odooCompatIssueSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('model'), model, message }),
  z.object({
    kind: z.literal('field'),
    model,
    field: z.string().regex(ODOO_FIELD_NAME_PATTERN).max(100),
    message,
  }),
  z.object({
    kind: z.literal('access'),
    model,
    operation: z.enum(ODOO_ACCESS_OPERATIONS),
    message,
  }),
]);
export type OdooCompatIssue = z.infer<typeof odooCompatIssueSchema>;

/** One tile: compatible exactly when nothing required is missing. */
export const odooCompatTileSchema = z
  .object({
    module: odooModuleIdSchema,
    compatible: z.boolean(),
    /** Required items Odoo lacks (the tile answers 409). */
    missing: z.array(odooCompatIssueSchema),
    /** Optional items Odoo lacks: only that part is off, as if its setting were empty. */
    optionalOff: z.array(odooCompatIssueSchema),
  })
  .refine((t) => t.compatible === (t.missing.length === 0), {
    message: 'A tile is compatible exactly when nothing required is missing',
    path: ['compatible'],
  });
export type OdooCompatTile = z.infer<typeof odooCompatTileSchema>;

/** Test connection's per-tile report (shown as `details` lines and on Admin › Modules). */
export const odooCompatReportSchema = z.object({
  /** `server_version` from Odoo, when it answered. */
  odooVersion: z.string().min(1).max(60).optional(),
  tiles: z.array(odooCompatTileSchema),
});
export type OdooCompatReport = z.infer<typeof odooCompatReportSchema>;

/** `error.details` of a 409 `ODOO_NOT_COMPATIBLE`: what the tile needs and Odoo lacks. */
export const odooNotCompatibleDetailsSchema = z.object({
  module: odooModuleIdSchema,
  missing: z.array(odooCompatIssueSchema).min(1),
});
export type OdooNotCompatibleDetails = z.infer<typeof odooNotCompatibleDetailsSchema>;
